// ── [BEGIN testing-mode gate — remove when public launch happens] ─
// Build-time gate that hides the app surface of the site while we're
// still in private testing. Set `VITE_TESTING_MODE=1` to enable. When
// active, every route except the exemptions below shows a splash-styled
// notice with no app entry points — the public can't sign up, sign in,
// enter class codes, or reach any teacher/student flow.
//
// Public routes (always pass through, even with TESTING_MODE on):
//   - ``/`` and ``/en``  — splash + marketing extension. Korean corporate
//     law requires the company surface to be publicly accessible. The
//     splash itself replaces its CTA buttons with the same testing
//     notice in this state (see SplashScreen.tsx) so a public visitor
//     can read the marketing copy but has no path into the app.
//   - ``/privacy``  — privacy policy. PIPA-required public document.
//
// Other exemptions:
//   - ``/dev/*``   — dev test-harness (only mounted when DEV_TOOLS=1)
//   - ``/admin/*`` — admin sign-in / dashboard. Operators reach this by
//     direct URL, not through any gated surface.
//   - dev-teacher token holder — once /dev/quickstart issues a token,
//     the post-quickstart flow keeps working.
//
// Holders of SITE_PASSWORD (testers, demoers, advisors with the link)
// can also bypass via the ``I have a password`` reveal in the
// TestingPasswordPanel. Correct passwords get stored in the same
// localStorage key SiteGate uses (``siteAccess``) — so the inner
// SiteGate transparently unlocks too.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { isDevTeacherActive } from '../api/devToken'
import QuestionBubbles from './QuestionBubbles'
import TestingPasswordPanel from './TestingPasswordPanel'

import s from './TestingModeGate.module.css'

// Read at call time (not module load) so test harnesses using
// vi.stubEnv can flip the value between tests. Vite still inlines
// the value at build time for production, so there's no runtime
// cost in the deployed bundle — the env-read becomes a constant.
const isTestingModeOn = () => import.meta.env.VITE_TESTING_MODE === '1'

const SITE_PASSWORD_KEY = 'siteAccess'

/**
 * Routes that stay publicly accessible even with TESTING_MODE active.
 * The corporate-website surface (splash + marketing extension +
 * privacy policy) is required to be reachable for incorporation /
 * PIPA reasons. App entry points (sign-in, sign-up, teacher,
 * student) stay gated.
 *
 * Mirror of the same list in SiteGate.tsx — kept inline (vs. a
 * shared module) because the list is short, both gates will be
 * removed at public launch, and a shared helper would outlive
 * its callers.
 */
function isPublicRoute(pathname: string): boolean {
  if (pathname === '/' || pathname === '/en') return true
  if (pathname === '/privacy' || pathname.startsWith('/privacy?')) return true
  return false
}

/**
 * Re-validate a stored password against the live backend. Drops the
 * cached value if the server rejects it (typical: SITE_PASSWORD was
 * rotated since the user last entered it).
 */
async function recheckStoredPassword(): Promise<boolean> {
  try {
    const stored = localStorage.getItem(SITE_PASSWORD_KEY)
    if (!stored) return false
    const res = await fetch('/api/probe', {
      method: 'GET',
      headers: { 'X-Site-Password': stored },
    })
    return res.status !== 401
  } catch {
    // Network failure → defensive false would log the user out on a
    // flaky connection. Trust the cached value; the next real API
    // call will surface any real auth issue.
    return true
  }
}

export default function TestingModeGate({ children }: { children: ReactNode }) {
  const location = useLocation()

  // Bypass state initialized from localStorage so a returning visitor
  // with the password skips the gate immediately. The inner SiteGate
  // reads the same key, so a single-prompt → both-gates-unlocked
  // flow works automatically.
  const [bypassed, setBypassed] = useState<boolean>(
    () => !!localStorage.getItem(SITE_PASSWORD_KEY),
  )

  // Re-validate a stored password on mount in case it was revoked
  // server-side. Defensive — same pattern SiteGate runs.
  useEffect(() => {
    if (!bypassed) return
    void recheckStoredPassword().then((ok) => {
      if (!ok) {
        localStorage.removeItem(SITE_PASSWORD_KEY)
        setBypassed(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for in-tab unlock signals from sibling gates / the splash's
  // embedded ``<TestingPasswordPanel>``. Without this, entering the
  // password via SplashScreen wouldn't propagate here — ``bypassed`` was
  // initialized at mount and would otherwise stay false until a remount,
  // re-prompting the user on the next gated route. Same family as #247
  // / PR #248. Closes #249.
  //
  // Trust note: the dispatchers fire only after a successful
  // ``/api/probe`` round-trip, so the listener can flip state without
  // re-validating. The mount-time effect above still covers the
  // returning-visitor case where localStorage held a stale-and-revoked
  // password.
  useEffect(() => {
    const onUnlock = () => setBypassed(true)
    window.addEventListener('sitegate:unlocked', onUnlock)
    return () => window.removeEventListener('sitegate:unlocked', onUnlock)
  }, [])

  // Cross-tab sync via the native ``storage`` event. Fires only in OTHER
  // tabs when this origin's localStorage changes — never in the
  // originating tab. So this listener handles "user just unlocked /
  // locked in tab B" → mirror to tab A's testing-notice panel
  // immediately, without forcing a refresh. The inner ``<SiteGate>``
  // has its own matching listener, but when this outer gate is showing
  // the testing-notice panel the inner gate isn't mounted yet — so
  // SiteGate's listener can't help and we need our own here. Closes #665.
  //
  // Sync directly from ``e.newValue`` (truthy = unlocked, null = locked)
  // rather than reading ``bypassed`` from the closure, matching the
  // shape SiteGate uses (see the trust + re-bind notes there).
  //
  // Trust note: another tab's ``setItem`` only happens after a
  // successful ``/api/probe`` round-trip there, so flipping locally
  // without re-validating is safe. The mount-time effect above still
  // covers the returning-visitor case where localStorage held a
  // stale-and-revoked password.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SITE_PASSWORD_KEY) return
      setBypassed(Boolean(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  if (!isTestingModeOn()) return <>{children}</>

  const path = location.pathname
  if (isPublicRoute(path)) return <>{children}</>
  if (path.startsWith('/dev/')) return <>{children}</>
  if (path.startsWith('/admin')) return <>{children}</>
  if (isDevTeacherActive()) return <>{children}</>
  if (bypassed) return <>{children}</>

  return (
    <div className={`splash ${s.testingGate}`}>
      <QuestionBubbles />
      <div className="splash-center">
        <div className="splash-logo-wrapper">
          <span className="splash-logo splash-logo--sm">eehae</span>
        </div>
        <TestingPasswordPanel onUnlocked={() => setBypassed(true)} />
      </div>
    </div>
  )
}
